import { Injectable } from '@angular/core';
import { AuthenticationService, BaseUrlService } from '@cbuild/core';
import { CsHttpClientService } from '@cbuild/cs-common-data-services';
import { toQueryString } from '@cbuild/shared';
import { catchError, map, Observable } from 'rxjs';
import { SoftPhoneUsers } from '../account-settings/user-access/models/user.model';
import { OrganizationConfiguration } from '../sales/leads/lead-activity/create-customer/models/organization-configuration.model';

export interface UserValidateResponse {
  token?: string;
  otpToken: string;
  isTfaEnable?: boolean;
  connectionKey: string;
}

export interface resetPasswordRequest {
  AliasName: string;
  OtpToken?: string;
  Otp?: string;
  UEmail?: string;
  Password?: string;
}

@Injectable()
export class LoginService {
  constructor(
    private http: CsHttpClientService,
    private baseUrlService: BaseUrlService,
    private authenticationService: AuthenticationService
  ) {}

  public login(data: {
    Username: string;
    Password: string;
  }): Observable<UserValidateResponse> {
    return this.http.writeJSON<UserValidateResponse>(
      'api/master/acquireTokenTfaCheck',
      data,
      this.baseUrlService.getIdentityBaseURL()
    );
  }

  public SetJWTToken(token: string): void {
    this.authenticationService.setJWTToken(token);
  }

  public validateOtp(data: {
    Username: string;
    Password: string;
    OtpToken: string;
    Otp: string;
    ConnectionKey: string;
  }): Observable<void> {
    return this.http
      .writeJSON<{ token: string }>(
        'api/master/validateUserOtp',
        data,
        this.baseUrlService.getIdentityBaseURL()
      )
      .pipe(
        map(res => {
          this.authenticationService.setJWTToken(res.token);
        })
      );
  }

  public resendOtp(
    data: UserValidateResponse
  ): Observable<UserValidateResponse> {
    return this.http.writeJSON<UserValidateResponse>(
      'api/master/resendOtp',
      data,
      this.baseUrlService.getIdentityBaseURL()
    );
  }

  public resetPassword(
    data: { password: string; phone: string; name: string },
    token: string
  ): Observable<{ userId: string }> {
    return this.http
      .writeJSON<Array<{ userId: string }>>(
        `api/account/reset-password?token=${token}`,
        data,
        this.baseUrlService.getIdentityBaseURL()
      )
      .pipe(
        map(resp => resp[0]),
        catchError(err => {
          throw err;
        })
      );
  }

  public resetPasswordCheck(
    token: string
  ): Observable<{
    name: string;
    phone: string;
  }> {
    return this.http.read(
      `api/account/reset-password-check?token=${token}`,
      this.baseUrlService.getIdentityBaseURL()
    );
  }

  public createUser(
    data: { password: string },
    token: string
  ): Observable<string> {
    return this.http
      .writeText<string>(
        `api/account/reset-password?token=${token}`,
        data,
        this.baseUrlService.getIdentityBaseURL()
      )
      .pipe(
        catchError(err => {
          throw err;
        })
      );
  }

  public saveUserName(data: {
    userId: string;
    name: string;
    phone: string;
  }): Observable<string> {
    return this.http
      .writeText<string>('api/account/user/saveUserName', data)
      .pipe(
        catchError(err => {
          throw err;
        })
      );
  }

  public getAliasName(
    email: string
  ): Observable<{ aliasName: string; email: string }> {
    return this.http
      .read<{ aliasName: string; email: string }>(
        `api/account/aliasName?uEmail=${email}`
      )
      .pipe(
        catchError(err => {
          throw err;
        })
      );
  }

  public sendOtp(data: resetPasswordRequest): Observable<{ otpToken: string }> {
    return this.http
      .writeText<{ otpToken: string }>(
        'api/account/user-reset-password-otp-send',
        data,
        this.baseUrlService.getApiBaseURL()
      )
      .pipe(
        catchError(err => {
          throw err;
        })
      );
  }

  public validateOTP(
    data: resetPasswordRequest
  ): Observable<{ status: string }> {
    return this.http
      .writeText<{ status: string }>(
        'api/account/user-reset-password-otp-verify',
        data
      )
      .pipe(
        catchError(err => {
          throw err;
        })
      );
  }

  public savePassword(
    data: resetPasswordRequest
  ): Observable<{ status: string }> {
    return this.http
      .writeText<{ status: string }>('api/account/reset-password', data)
      .pipe(
        catchError(err => {
          throw err;
        })
      );
  }

  public getSoftPhoneConfiguration(
    id: number
  ): Observable<OrganizationConfiguration> {
    return this.http
      .read<Array<OrganizationConfiguration>>(
        `api/settings/general-settings/get${toQueryString({
          id
        })}`
      )
      .pipe(map(_ => _[0]));
  }

  public getUserSoftPhoneDetails(): Observable<SoftPhoneUsers> {
    return this.http
      .read<Array<SoftPhoneUsers>>(`api/account/user/softPhone/details`)
      .pipe(map(_ => _[0]));
  }

  public softPhoneLogin(
    data: {
      api_key: string;
      username: string;
      password: string;
      type: string;
    },
    type: string
  ): Observable<{ callingmode: string; msg: string; status: string }> {
    return this.http
      .writeText<{ callingmode: string; msg: string; status: string }>(
        `api/account/soft-phone-login${toQueryString({
          type
        })}`,
        data
      )
      .pipe(
        catchError(err => {
          throw err;
        })
      );
  }
}
